import React, { memo, useCallback, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HomeTimeCapsuleMapV2 from "./HomeTimeCapsuleMapV2";

const TILT_PRESETS = {
  low: { label: "2D-ish", tilt: 0, heading: 0 },
  mid: { label: "55°", tilt: 55, heading: 0 },
  immersive: { label: "Immersive", tilt: 67, heading: 0 },
};

const DEBUG_HOME_MAP_SHOVEL =
  String(
    process.env.REACT_APP_DEBUG_HOME_MAP_SHOVEL ||
      (typeof window !== "undefined"
        ? window.localStorage?.getItem("debugHomeMapShovel")
        : "") ||
      ""
  ).toLowerCase() === "true";
const DEFAULT_TILT_PRESET = "mid";
const MARKER_SCALE_MIN = 0.72;
const MARKER_SCALE_MAX = 1.24;
const MARKER_SCALE_FULL_DISTANCE_METERS = 120;
const MARKER_SCALE_MIN_DISTANCE_METERS = 1400;

function isLatLngLiteral(value) {
  return (
    value &&
    typeof value === "object" &&
    Number.isFinite(Number(value.lat)) &&
    Number.isFinite(Number(value.lng))
  );
}

function distanceMeters(a, b) {
  if (!isLatLngLiteral(a) || !isLatLngLiteral(b)) return MARKER_SCALE_MIN_DISTANCE_METERS;
  const toRad = (degrees) => (Number(degrees) * Math.PI) / 180;
  const lat1 = Number(a.lat);
  const lat2 = Number(b.lat);
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(Number(b.lng) - Number(a.lng));
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function getDistanceScale(position, center) {
  const distance = distanceMeters(position, center);
  if (distance <= MARKER_SCALE_FULL_DISTANCE_METERS) return MARKER_SCALE_MAX;
  if (distance >= MARKER_SCALE_MIN_DISTANCE_METERS) return MARKER_SCALE_MIN;

  const progress =
    (distance - MARKER_SCALE_FULL_DISTANCE_METERS) /
    (MARKER_SCALE_MIN_DISTANCE_METERS - MARKER_SCALE_FULL_DISTANCE_METERS);
  return MARKER_SCALE_MAX - progress * (MARKER_SCALE_MAX - MARKER_SCALE_MIN);
}

function scaleDimension(value, scale) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return value;
  return Math.max(1, Math.round(number * scale));
}

function scaleDisplayItemsByDistance(displayItems, center) {
  if (!isLatLngLiteral(center)) return Array.isArray(displayItems) ? displayItems : [];
  return (Array.isArray(displayItems) ? displayItems : []).map((item) => {
    if (!item?.position) return item;
    const scale = getDistanceScale(item.position, center);

    if (item.kind === "cluster") {
      return {
        ...item,
        displayScale: scale,
      };
    }

    if (!item.marker) return item;
    return {
      ...item,
      marker: {
        ...item.marker,
        width: scaleDimension(item.marker.width, scale),
        height: scaleDimension(item.marker.height, scale),
      },
    };
  });
}

function getRoadmapTypeId() {
  return window.google?.maps?.MapTypeId?.ROADMAP || "roadmap";
}

function getVectorRenderingType() {
  return window.google?.maps?.RenderingType?.VECTOR || "VECTOR";
}

function getMapColorScheme(mode) {
  const colorScheme = window.google?.maps?.ColorScheme;
  if (mode === "dark") return colorScheme?.DARK || "DARK";
  return colorScheme?.LIGHT || "LIGHT";
}

function nearlyEqualCameraValue(left, right) {
  const a = Number(left);
  const b = Number(right);
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 0.5;
}

function applyGraphicalCamera(map, presetKey, options = {}) {
  if (!map) return;
  const preset = TILT_PRESETS[presetKey] || TILT_PRESETS.mid;
  const { force = false } = options;
  try {
    map.setMapTypeId?.(getRoadmapTypeId());
    const currentTilt = map.getTilt?.();
    const currentHeading = map.getHeading?.();
    if (force || !nearlyEqualCameraValue(currentTilt, preset.tilt)) {
      map.setTilt?.(preset.tilt);
    }
    if (force || !nearlyEqualCameraValue(currentHeading, preset.heading)) {
      map.setHeading?.(preset.heading);
    }
  } catch (error) {
    console.warn("[home-map-graphical-3d] Camera update failed:", error);
  }
}

const HomeGraphicalTiltMap3D = memo(function HomeGraphicalTiltMap3D({
  center,
  userLocation,
  displayItems,
  directions,
  onLoad,
  onDrag,
  onDragEnd,
  onIdle,
  onMarkerClick,
  onClusterClick,
  onLongPress,
  onPoiClick,
  mapOptions,
  containerStyle,
  onFallbackTo2D,
}) {
  const theme = useTheme();
  const mapRef = useRef(null);

  const graphicalMapOptions = useMemo(
    () => ({
      ...mapOptions,
      mapTypeId: getRoadmapTypeId(),
      renderingType: getVectorRenderingType(),
      colorScheme: getMapColorScheme(theme.palette.mode),
      tilt: TILT_PRESETS[DEFAULT_TILT_PRESET].tilt,
      heading: TILT_PRESETS[DEFAULT_TILT_PRESET].heading,
      rotateControl: false,
    }),
    [mapOptions, theme.palette.mode]
  );
  const scaledDisplayItems = useMemo(
    () => scaleDisplayItemsByDistance(displayItems, center),
    [center, displayItems]
  );

  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      try {
        applyGraphicalCamera(map, DEFAULT_TILT_PRESET, { force: true });
      } catch (error) {
        onFallbackTo2D?.(error);
        return;
      }
      onLoad?.(map);
    },
    [onFallbackTo2D, onLoad]
  );

  const handleMapIdle = useCallback(
    (event) => {
      if (DEBUG_HOME_MAP_SHOVEL) {
        console.log("[home-map-shovel] graphical onIdle", { at: Date.now() });
      }
      applyGraphicalCamera(mapRef.current, DEFAULT_TILT_PRESET);
      onIdle?.(event);
    },
    [onIdle]
  );

  const handleMapDragEnd = useCallback(
    (event) => {
      if (DEBUG_HOME_MAP_SHOVEL) {
        console.log("[home-map-shovel] graphical onDragEnd", { at: Date.now() });
      }
      applyGraphicalCamera(mapRef.current, DEFAULT_TILT_PRESET);
      onDragEnd?.(event);
    },
    [onDragEnd]
  );

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <HomeTimeCapsuleMapV2
        key={`graphical-map-${theme.palette.mode}`}
        center={center}
        userLocation={userLocation}
        displayItems={scaledDisplayItems}
        directions={directions}
        onLoad={handleMapLoad}
        onDrag={onDrag}
        onDragEnd={handleMapDragEnd}
        onIdle={handleMapIdle}
        onMarkerClick={onMarkerClick}
        onClusterClick={onClusterClick}
        onLongPress={onLongPress}
        onPoiClick={onPoiClick}
        mapOptions={graphicalMapOptions}
        containerStyle={containerStyle}
      />
    </Box>
  );
});

export default HomeGraphicalTiltMap3D;
